import { useMutation, useQuery, useQueryClient } from '../useQuery'
import { queryKeys } from '@/api/queryKeys'
import * as customerApi from '@/api/customer'
import type {
  CreateCustomerContactRequest,
  CreateCustomerContractRequest,
  CreateCustomerRequest,
  CreateCustomerSiteRequest,
  CustomerPageParams,
} from '@/api/types/customer'

export function useCustomerPage(params: CustomerPageParams = {}) {
  return useQuery({ queryKey: queryKeys.customers(params), queryFn: () => customerApi.getCustomerPage(params) })
}

export function useCustomer(id: number) {
  return useQuery({ queryKey: queryKeys.customer(id), queryFn: () => customerApi.getCustomer(id), enabled: !!id })
}

function useCustomerRefresh() {
  const queryClient = useQueryClient()
  return (id?: number) => {
    queryClient.invalidateQueries({ queryKey: ['customers'] })
    if (id) queryClient.invalidateQueries({ queryKey: queryKeys.customer(id) })
  }
}

export function useCreateCustomer() {
  const refresh = useCustomerRefresh()
  return useMutation({ mutationFn: (data: CreateCustomerRequest) => customerApi.createCustomer(data), onSuccess: () => refresh() })
}

export function useUpdateCustomer(id: number) {
  const refresh = useCustomerRefresh()
  return useMutation({ mutationFn: (data: CreateCustomerRequest) => customerApi.updateCustomer(id, data), onSuccess: () => refresh(id) })
}

export function useCreateCustomerSite(id: number) {
  const refresh = useCustomerRefresh()
  return useMutation({ mutationFn: (data: CreateCustomerSiteRequest) => customerApi.createCustomerSite(id, data), onSuccess: () => refresh(id) })
}

export function useCreateCustomerContact(id: number) {
  const refresh = useCustomerRefresh()
  return useMutation({ mutationFn: (data: CreateCustomerContactRequest) => customerApi.createCustomerContact(id, data), onSuccess: () => refresh(id) })
}

export function useCreateCustomerContract(id: number) {
  const refresh = useCustomerRefresh()
  return useMutation({ mutationFn: (data: CreateCustomerContractRequest) => customerApi.createCustomerContract(id, data), onSuccess: () => refresh(id) })
}

export function useLinkCustomerDevice(id: number) {
  const refresh = useCustomerRefresh()
  return useMutation({ mutationFn: (deviceId: number) => customerApi.linkDeviceAsset(id, deviceId), onSuccess: () => refresh(id) })
}
